import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { ChevronDown, ChevronUp, GripVertical, Plus, X } from "lucide-react";
import type { ScrapedPhoto } from "@/lib/newPropertyWizard";

interface Step3RoomsProps {
  rooms: string[];
  scrapedPhotos?: ScrapedPhoto[];
  onChange: (rooms: string[]) => void;
}

export function Step3Rooms({ rooms, scrapedPhotos = [], onChange }: Step3RoomsProps) {
  const [newRoom, setNewRoom] = useState("");

  const addRoom = () => {
    const trimmed = newRoom.trim();
    if (!trimmed || rooms.some((r) => r.toLowerCase() === trimmed.toLowerCase())) return;
    onChange([...rooms, trimmed]);
    setNewRoom("");
  };

  const removeRoom = (index: number) => {
    onChange(rooms.filter((_, i) => i !== index));
  };

  const moveRoom = (index: number, dir: -1 | 1) => {
    const next = index + dir;
    if (next < 0 || next >= rooms.length) return;
    const copy = [...rooms];
    [copy[index], copy[next]] = [copy[next], copy[index]];
    onChange(copy);
  };

  const photosForRoom = (roomName: string) =>
    scrapedPhotos.filter((p) => {
      if (!p.roomLabel) return false;
      const label = p.roomLabel.toLowerCase();
      const name = roomName.toLowerCase();
      return name.includes(label) || label.includes(name);
    });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Rooms</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Rooms are used to organize tasks, inventory, and photos. You can add more later.
        </p>
      </div>

      <ul className="space-y-2">
        {rooms.map((room, index) => {
          const thumbs = photosForRoom(room).slice(0, 6);
          return (
            <li
              key={`${room}-${index}`}
              className="flex flex-col gap-2 rounded-lg border bg-card px-3 py-2"
            >
              <div className="flex items-center gap-2">
                <GripVertical className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden />
                <span className="flex-1 font-medium text-sm truncate">{room}</span>
                <div className="flex items-center gap-0.5 shrink-0">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => moveRoom(index, -1)}
                    disabled={index === 0}
                    aria-label="Move up"
                  >
                    <ChevronUp className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => moveRoom(index, 1)}
                    disabled={index === rooms.length - 1}
                    aria-label="Move down"
                  >
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-destructive"
                    onClick={() => removeRoom(index)}
                    aria-label="Remove room"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              {thumbs.length > 0 && (
                <div className="flex gap-1.5 overflow-x-auto pb-1 pl-6">
                  {thumbs.map((p, i) => (
                    <img
                      key={`${p.url}-${i}`}
                      src={p.url}
                      alt=""
                      className={cn("h-10 w-10 rounded object-cover shrink-0 border")}
                    />
                  ))}
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {rooms.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-4">Add at least one room to continue.</p>
      )}

      <div className="flex gap-2">
        <Input
          value={newRoom}
          onChange={(e) => setNewRoom(e.target.value)}
          placeholder="Room name"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addRoom();
            }
          }}
        />
        <Button type="button" variant="secondary" onClick={addRoom} disabled={!newRoom.trim()}>
          <Plus className="h-4 w-4 sm:mr-1" />
          <span className="hidden sm:inline">Add</span>
        </Button>
      </div>
    </div>
  );
}
